import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import DraftBanner from "@/components/DraftBanner";
import HreflangTags from "@/components/HreflangTags";

const ScrollHandler = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (attempts < 12) {
          attempts += 1;
          window.setTimeout(tryScroll, 40);
        }
      };
      tryScroll();
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);
  return null;
};

/* Reveal-on-scroll — fires once per element via IntersectionObserver.
   MutationObserver keeps it responsive to route changes. */
const useRevealOnScroll = () => {
  useEffect(() => {
    if (!("IntersectionObserver" in window)) {
      document.querySelectorAll(".reveal").forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -30px 0px" }
    );
    const observe = () => {
      document.querySelectorAll(".reveal:not(.is-visible)").forEach((el) => io.observe(el));
    };
    observe();
    const mo = new MutationObserver(() => observe());
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { io.disconnect(); mo.disconnect(); };
  }, []);
};

/* Eyebrow crossfade — fades small uppercase eyebrow strips to 30%
   whenever they leave the central viewport band, back to 100% on entry. */
const useEyebrowCrossfade = () => {
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.remove("eyebrow-out");
          else e.target.classList.add("eyebrow-out");
        });
      },
      { rootMargin: "0px 0px 0px 0px", threshold: 0 }
    );
    const observe = () => {
      document.querySelectorAll(".eyebrow").forEach((el) => io.observe(el));
    };
    observe();
    const mo = new MutationObserver(() => observe());
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { io.disconnect(); mo.disconnect(); };
  }, []);
};

/* Universal image parallax — 90% scroll speed on all `.parallax-inner`.
   Uses rAF-throttled scroll listener and getBoundingClientRect for offset.
   Bails out entirely under prefers-reduced-motion. */
const useParallax = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mql.matches) return;

    let queued = false;
    let rafId = 0;

    const update = () => {
      queued = false;
      const vh = window.innerHeight;
      document.querySelectorAll(".parallax-inner").forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -100 || rect.top > vh + 100) return;
        const center = rect.top + rect.height / 2;
        // 92% scroll speed => image lags by 8% of distance from viewport center
        const raw = (center - vh / 2) * -0.08;
        const clamped = Math.max(-36, Math.min(36, raw));
        el.style.transform = `translate3d(0, ${clamped.toFixed(2)}px, 0)`;
      });
    };

    const onScroll = () => {
      if (!queued) {
        queued = true;
        rafId = requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
};

/* Cinematic Ken Burns assignment — for every hero-media and every
   editorial <img> inside a .mask-reveal, pick one of 4 directional
   variants (a/b/c/d) and a stable phase offset (0-59s) based on a
   deterministic hash of the img src, then set the data-kb attribute
   and inline animation-delay. This guarantees no two adjacent images
   drift in perfect sync and each image is always in slow motion.
   Under prefers-reduced-motion the CSS ignores the assignment
   entirely (`animation: none !important`). */
const useKenBurns = () => {
  useEffect(() => {
    const variants = ["a", "b", "c", "d"];
    const assign = () => {
      const nodes = document.querySelectorAll(".mask-reveal img, .hero-media");
      nodes.forEach((img) => {
        if (img.dataset.kb) return;
        const src = img.getAttribute("src") || img.getAttribute("data-src") || "";
        let h = 5381;
        for (let i = 0; i < src.length; i++) h = ((h * 33) ^ src.charCodeAt(i)) >>> 0;
        img.dataset.kb = variants[h % variants.length];
        // Negative delay so the animation starts mid-cycle for phase variety.
        img.style.animationDelay = `-${h % 60}s`;
      });
    };
    assign();
    const mo = new MutationObserver(() => assign());
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);
};

const MotionBridge = () => {
  useRevealOnScroll();
  useEyebrowCrossfade();
  useParallax();
  useKenBurns();
  return null;
};

export const SiteLayout = () => {
  return (
    <div className="min-h-screen bg-cream text-navy flex flex-col">
      <ScrollHandler />
      <MotionBridge />
      <HreflangTags />
      <Header />
      <DraftBanner />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <CookieBanner />
    </div>
  );
};

export default SiteLayout;
